import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { isValidArray } from "../../../Layout/Common/common";
import { GlobalContext } from "../../../Context/GlobalContext";
import { SHIPPING_METHOD } from "../../../constants";
import { useGlobalFunction } from "../../../Context/GlobalFunctionContext";
import { useNoteAlert } from "../../../Context/pleaseNote";
import { CourierDelivery } from "./MethodSelection/courierDelivery";
import { StorePickup } from "./MethodSelection/storePickup";
import { CourierPickup } from "./MethodSelection/courierPickup";
import { takePendingDeliveryMethod } from "../../../Context/hook/tillRedirect";
import { useParams } from "react-router-dom";

export const MethodSelection = () => {
  const courierTabRef = useRef(null);
  const deliveryTabRef = useRef(null);
  const storeTabRef = useRef(null);
  const { deliveryMethod, setDeliveryMethod, shippingMode, IsInterNational, pickupOnlyProduct } =
    useContext(GlobalContext);
  const [pickupOnly, setPickupOnly] = useState(null);
  const [internationalUser, setInternationalUser] = useState(null);
  const { clearSelectedShipService } = useGlobalFunction();
  const { showDialogAlert, hideNoteAlert } = useNoteAlert();
  const { orderId } = useParams();
  // Holds { method, clear } while a programmatic pill click is in flight.
  const pendingClickRef = useRef(null);

  // deliveryTabRef is the Courier Pickup tab - the name predates the third method.
  const tabRefFor = (method) =>
    method === SHIPPING_METHOD[2] ? storeTabRef : method === SHIPPING_METHOD[3] ? deliveryTabRef : courierTabRef;

  // Programmatic pill selection must not re-open the confirm dialog. clear = false
  // reflects a method that is already in context, so it keeps the shipping service.
  const selectTab = (method, clear = true) => {
    const target = tabRefFor(method);
    if (!target.current) return;
    pendingClickRef.current = { method, clear };
    target.current.click();
  };

  // deliveryMethod is the source of truth; fall back to shippingMode when a pending
  // order came back with a pickup label as its shipping service.
  const activeMethod = [SHIPPING_METHOD[2], SHIPPING_METHOD[3]].includes(deliveryMethod)
    ? deliveryMethod
    : [SHIPPING_METHOD[2], SHIPPING_METHOD[3]].includes(shippingMode)
      ? shippingMode
      : deliveryMethod;

  useEffect(() => {
    if (pickupOnlyProduct && isValidArray(pickupOnlyProduct)) setPickupOnly(true);
    else setPickupOnly(false);
    if (IsInterNational != null) setInternationalUser(IsInterNational);
  }, [pickupOnlyProduct, IsInterNational]);

  useEffect(() => {
    if (internationalUser == true) {
      selectTab(SHIPPING_METHOD[1]);
      setDeliveryMethod(SHIPPING_METHOD[1]);
    }
  }, [internationalUser]);
  useEffect(() => {
    if (pickupOnly == true && storeTabRef.current) {
      selectTab(SHIPPING_METHOD[2]);
      setDeliveryMethod(SHIPPING_METHOD[2]);
    }
  }, [pickupOnly]);

  useEffect(() => {
    if (pickupOnly === null) return;
    const restored = takePendingDeliveryMethod();
    if (!restored || restored === SHIPPING_METHOD[1]) return;
    if (internationalUser === true || pickupOnly === true) return;
    selectTab(restored);
  }, [pickupOnly, internationalUser]);

  // Reflect the order's saved method in the pill strip. Only clicks when the pill is
  // not already active, so the setDeliveryMethod it triggers cannot loop.
  useEffect(() => {
    if (!activeMethod || internationalUser === true || pickupOnly === true) return;
    const target = tabRefFor(activeMethod);
    if (target.current && !target.current.classList.contains("active")) selectTab(activeMethod, false);
  }, [activeMethod, internationalUser, pickupOnly]);

  const applyMethod = (method) => {
    clearSelectedShipService();
    setDeliveryMethod(method);
  };

  const handleChangeMethod = (e, method) => {
    const pending = pendingClickRef.current;
    if (pending) {
      pendingClickRef.current = null;
      if (pending.clear) applyMethod(method);
      else setDeliveryMethod(method);
      return;
    }
    if (!orderId || method === activeMethod) {
      applyMethod(method);
      return;
    }
    // React delegates below document, so stopping here keeps Bootstrap from
    // switching the pill while the confirmation is open.
    e.preventDefault();
    e.stopPropagation();
    showDialogAlert({
      title: "Change Delivery Method",
      content: "Changing the delivery method will clear the shipping service saved on this order. Do you want to continue?",
      primaryBtn: {
        caption: "Yes, Change",
        onClick: () => {
          selectTab(method);
          hideNoteAlert();
        },
      },
      scondaryBtn: { caption: "Cancel", onClick: hideNoteAlert },
    });
  };

  return (
    <div className="nav nav-pills delivery-method-pills mb-3" id="pills-tab" role="tablist">
      {internationalUser == true ? (
        <CourierDelivery tabRef={courierTabRef} handleChangeMethod={handleChangeMethod} />
      ) : pickupOnly == true ? (
        <StorePickup tabRef={storeTabRef} handleChangeMethod={handleChangeMethod} />
      ) : (
        <Fragment>
          <CourierDelivery tabRef={courierTabRef} handleChangeMethod={handleChangeMethod} />
          <StorePickup tabRef={storeTabRef} handleChangeMethod={handleChangeMethod} />
          <CourierPickup tabRef={deliveryTabRef} handleChangeMethod={handleChangeMethod} />
        </Fragment>
      )}
    </div>
  );
};
